import React, { useEffect, useCallback } from 'react';
import { Form } from 'src/core/forms';
import { useDispatch } from 'react-redux';
import { paths } from 'src/config';
import { ButtonLink } from 'src/components/Button/ButtonLink';
import { useFieldValue } from 'src/core/forms';
import { ARTWORK_EXISTING } from 'src/components/Artwork/artwork.types';
import { CustomizationSkeleton } from 'src/elements/Skeletons/Skeletons';
import { RegularArtworkSelect } from 'src/components/Artwork/RegularArtworkSelect';
import { NEW_ARTWORK_ID, EMPTY_ARTWORK_ID, HAVE_MY_LOGO, LOGO_EXIST } from 'src/constants/artwork';
import { artWorkEntryUrl } from 'src/components/CustomisationPage/utils/customisationsUrls';
import { push } from 'src/core/engine/router/index';
import { Button } from 'src/components/Button/Button';
import { nameOfSelectedMethod, nameOfNotAvailableMethod } from 'src/util/nameOfMethod';
import { AutomaticallySelectedMethod } from 'src/components/BundleProduct/AutomaticallySelectedMethod';
import Bugsnag from '@bugsnag/js';

import { Actions } from '../../Forms/Actions';
import { InfoBlock } from '../InfoBlock';
import { useCustomisation } from '../hooks/useCustomisation';
import { CustomisationFull, Fields, Step, ApplicationMethod } from '../feature/customisation.types';
import { CustomisationMsg, EntryKind } from '../feature/customisation.actions';

import classes from './Pages.scss';

type Props = {
    submit(values?: { [index: string]: any });
    artworkIsMissing?: boolean;
    artworkId?: number;
    oneMethodAvailable?: boolean;
    itemIds?: number[];
    selectedMethod?: ApplicationMethod;
    someLocationHasBothPositions?: boolean;
    productHasOnlyOnePosition?: boolean;
};

export const Artwork: React.FC<Props> = ({
    submit,
    artworkIsMissing,
    artworkId,
    oneMethodAvailable,
    itemIds,
    selectedMethod,
    someLocationHasBothPositions,
    productHasOnlyOnePosition,
}) => {
    return (
        <div className={classes.fadein}>
            <h2 className={classes.header}>3. Choose and add your logo</h2>
            {oneMethodAvailable && !productHasOnlyOnePosition && (
                <AutomaticallySelectedMethod
                    nameOfNotAvailableMethod={nameOfNotAvailableMethod(Number(selectedMethod))}
                    nameOfSelectedMethod={nameOfSelectedMethod(Number(selectedMethod))}
                    className={classes.additionalInfo}
                    someLocationHasBothPositions={someLocationHasBothPositions}
                />
            )}
            <Form className={classes.form} onSubmit={submit}>
                <ArtworkInner
                    artworkIsMissing={artworkIsMissing}
                    artworkId={artworkId}
                    oneMethodAvailable={oneMethodAvailable}
                    itemIds={itemIds}
                />
            </Form>
            <InfoBlock />
        </div>
    );
};
export const ArtworkInner: React.FC<{
    artworkIsMissing?: boolean;
    artworkId?: number;
    oneMethodAvailable?: boolean;
    itemIds?: number[];
}> = ({ artworkId, oneMethodAvailable, itemIds }) => {
    const value = useFieldValue(Fields.artwork);
    const dispatch = useDispatch();

    const customiseProduct = useCallback(() => {
        const path = artWorkEntryUrl({ itemIds: [Number(itemIds)!], kind: EntryKind.New });
        dispatch(push(path));
    }, [dispatch, itemIds]);

    return (
        <>
            <RegularArtworkSelect existingArtworkId={artworkId} />

            <Actions
                primaryText={value === NEW_ARTWORK_ID ? 'Continue' : 'Finish'}
                primaryVariant={value === NEW_ARTWORK_ID ? 'primary' : 'secondary'}
                primaryDisabled={!value || value === EMPTY_ARTWORK_ID}
                variant="space-between"
                direction="reverse"
            >
                {oneMethodAvailable ? (
                    <Button variant="light" onClick={customiseProduct}>
                        Back a step
                    </Button>
                ) : (
                    <ButtonLink variant="light" to={paths.customisation.method}>
                        Back a step
                    </ButtonLink>
                )}
            </Actions>
        </>
    );
};

export const PageArtworkConnected: React.FC = () => {
    const { loadStep, isPending, state } = useCustomisation();
    const dispatch = useDispatch();
    const artworkIsMissing = state.is_artwork_missing;
    const artworkId = state.id as number;

    const someLocationHasBothPositions = Boolean(
        state.availableLocations?.find((location) => location.method !== state.application_method),
    );

    const productHasOnlyOnePosition = Boolean(
        !state.availableLocations?.find((location) => location.method !== state.availableLocations?.[0]?.method) &&
        !someLocationHasBothPositions,
    );

    function submitArtwork(values) {
        // New logo without chosing existing artwork
        if (values[Fields.artwork] === NEW_ARTWORK_ID) {
            dispatch(CustomisationMsg('Customisation.ArtworkSubmitNew'));
        } else {
            try {
                let obj: CustomisationFull = defaultObj;

                if (values[ARTWORK_EXISTING] && values[Fields.artwork] !== HAVE_MY_LOGO) {
                    obj = JSON.parse(values[ARTWORK_EXISTING]);
                } else if ([LOGO_EXIST, HAVE_MY_LOGO].includes(values[Fields.artwork])) {
                    // Artwork exist but is not showed in the artwork list
                    const haveMyLogo = values[Fields.artwork] === HAVE_MY_LOGO;
                    obj = {
                        color: haveMyLogo ? '' : 'White',
                        customisation_name: '',
                        font: haveMyLogo ? '' : 'Arial',
                        image: '',
                        is_contact_requested: true,
                        is_free: false,
                        is_artwork_missing: true,
                        special_instructions: '',
                        text_line_one: '',
                        text_line_three: '',
                        text_line_two: '',
                        type: state.application_method || 2,
                    };

                    if (Number(state.id)) {
                        obj.id = Number(state.id);
                        obj.artwork_id = 0;
                    }
                }

                dispatch(CustomisationMsg('Customisation.ArtworkSubmitExisting', obj));
            } catch (e) {
                Bugsnag.notify('Could not deserialize the customisation', (event) => {
                    event.addMetadata('errrorObject', e as any);
                });
            }
        }
    }

    useEffect(() => {
        setTimeout(() => {
            loadStep({ step: Step.Artwork });
        }, 0);
    }, [loadStep]);

    if (isPending) return <CustomizationSkeleton />;

    return (
        <Artwork
            submit={submitArtwork}
            artworkIsMissing={artworkIsMissing}
            artworkId={artworkId}
            oneMethodAvailable={state.one_method}
            itemIds={state.item_ids}
            selectedMethod={state.application_method}
            someLocationHasBothPositions={someLocationHasBothPositions}
            productHasOnlyOnePosition={productHasOnlyOnePosition}
        />
    );
};

const defaultObj = {
    customisation_name: '',
    image: '',
    type: 1,
    is_contact_requested: false,
    special_instructions: '',
    text_line_one: '',
    text_line_two: '',
    text_line_three: '',
    color: '',
    font: '',
    is_artwork_missing: false,
    is_free: false,
};
